import { motion } from 'framer-motion'
import ScrollReveal from '../components/shared/ScrollReveal'
import GradientText from '../components/shared/GradientText'
import GlowButton from '../components/shared/GlowButton'
import './DraftPages.css'

const Icons = {
  cookie: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10" />
      <circle cx="8" cy="9" r="1" fill="currentColor" />
      <circle cx="15" cy="8" r="1" fill="currentColor" />
      <circle cx="10" cy="14" r="1" fill="currentColor" />
      <circle cx="16" cy="14" r="1" fill="currentColor" />
      <circle cx="12" cy="11" r="1" fill="currentColor" />
    </svg>
  ),
  shield: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  ),
  settings: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  ),
  barChart: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <line x1="12" y1="20" x2="12" y2="10" />
      <line x1="18" y1="20" x2="18" y2="4" />
      <line x1="6" y1="20" x2="6" y2="16" />
    </svg>
  ),
  target: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10" />
      <circle cx="12" cy="12" r="6" />
      <circle cx="12" cy="12" r="2" />
    </svg>
  ),
  mail: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
      <polyline points="22,6 12,13 2,6" />
    </svg>
  ),
  check: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  ),
  x: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  )
}

const cookieTypes = [
  {
    icon: Icons.shield,
    name: 'Essential Cookies',
    required: true,
    description: 'Required for the platform to function. Cannot be disabled.',
    examples: [
      'Authentication and session management',
      'Security tokens and CSRF protection',
      'Load balancing and server routing',
      'User preference settings (language, timezone)'
    ],
    retention: 'Session to 1 year'
  },
  {
    icon: Icons.barChart,
    name: 'Analytics Cookies',
    required: false,
    description: 'Help us understand how users interact with our platform.',
    examples: [
      'Page views and navigation patterns',
      'Feature usage statistics',
      'Error tracking and performance monitoring',
      'User journey analysis'
    ],
    retention: 'Up to 2 years'
  },
  {
    icon: Icons.settings,
    name: 'Functional Cookies',
    required: false,
    description: 'Enable enhanced functionality and personalization.',
    examples: [
      'Remembering dashboard layouts',
      'Preferred chart types and views',
      'Recent searches and filters',
      'Notification preferences'
    ],
    retention: 'Up to 1 year'
  },
  {
    icon: Icons.target,
    name: 'Marketing Cookies',
    required: false,
    description: 'Used to deliver relevant advertisements and measure campaigns.',
    examples: [
      'Ad targeting and retargeting',
      'Campaign effectiveness measurement',
      'Cross-site tracking for ads',
      'Social media integration'
    ],
    retention: 'Up to 2 years'
  }
]

const thirdPartyCookies = [
  { name: 'Google Analytics', purpose: 'Website analytics and user behavior tracking', type: 'Analytics' },
  { name: 'Stripe', purpose: 'Payment processing and fraud prevention', type: 'Essential' },
  { name: 'Intercom', purpose: 'Customer support chat and messaging', type: 'Functional' },
  { name: 'LinkedIn', purpose: 'Social sharing and advertising', type: 'Marketing' }
]

export default function Cookies() {
  const lastUpdated = 'January 28, 2025'

  return (
    <div className="draft-page privacy-page">
      {/* Hero */}
      <section className="draft-hero">
        <div className="container">
          <ScrollReveal>
            <span className="draft-badge">Legal</span>
          </ScrollReveal>
          <ScrollReveal delay={0.1}>
            <h1 className="draft-title">
              Cookie{' '}
              <GradientText gradient="energy">Policy</GradientText>
            </h1>
          </ScrollReveal>
          <ScrollReveal delay={0.2}>
            <p className="draft-subtitle">
              This Cookie Policy explains how AI4Renewables uses cookies and similar tracking
              technologies when you visit our website and use the AI4Solar platform.
            </p>
          </ScrollReveal>
          <ScrollReveal delay={0.3}>
            <p style={{ color: 'var(--color-gray-400)', fontSize: '0.875rem', marginTop: 'var(--space-md)' }}>
              Last updated: {lastUpdated}
            </p>
          </ScrollReveal>
        </div>
      </section>

      {/* What Are Cookies */}
      <section className="about-section">
        <div className="container">
          <ScrollReveal className="section-header">
            <h2>What Are Cookies?</h2>
            <p>Understanding web tracking technologies</p>
          </ScrollReveal>

          <ScrollReveal delay={0.1}>
            <div className="legal-content">
              <p>
                Cookies are small text files that are stored on your device (computer, tablet, or mobile)
                when you visit a website. They are widely used to make websites work more efficiently,
                provide a better user experience, and give website owners information about how
                their site is being used.
              </p>
              <p style={{ marginTop: 'var(--space-lg)' }}>
                In addition to cookies, we may also use similar technologies such as:
              </p>
              <ul style={{ marginTop: 'var(--space-md)', marginLeft: 'var(--space-lg)' }}>
                <li style={{ marginBottom: 'var(--space-sm)', color: 'var(--color-gray-300)' }}>
                  <strong style={{ color: 'var(--color-white)' }}>Local Storage:</strong> Similar to cookies but with larger storage capacity
                </li>
                <li style={{ marginBottom: 'var(--space-sm)', color: 'var(--color-gray-300)' }}>
                  <strong style={{ color: 'var(--color-white)' }}>Session Storage:</strong> Temporary storage that is cleared when you close your browser
                </li>
                <li style={{ marginBottom: 'var(--space-sm)', color: 'var(--color-gray-300)' }}>
                  <strong style={{ color: 'var(--color-white)' }}>Pixel Tags:</strong> Small images used to track user behavior and email opens
                </li>
              </ul>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Types of Cookies */}
      <section className="about-section about-dark">
        <div className="container">
          <ScrollReveal className="section-header">
            <h2>Types of Cookies We Use</h2>
            <p>Categories of cookies on our platform</p>
          </ScrollReveal>

          <div className="values-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
            {cookieTypes.map((cookie, i) => (
              <ScrollReveal key={i} delay={i * 0.1}>
                <motion.div
                  className="value-card docs-card"
                  whileHover={{ y: -5 }}
                  style={{ height: '100%' }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-md)', marginBottom: 'var(--space-md)' }}>
                    <span className="value-icon" style={{ margin: 0 }}>{cookie.icon}</span>
                    <div>
                      <h3 style={{ marginBottom: '4px' }}>{cookie.name}</h3>
                      <span style={{
                        fontSize: '0.75rem',
                        padding: '2px 8px',
                        borderRadius: 'var(--radius-sm)',
                        background: cookie.required ? 'rgba(255, 215, 0, 0.2)' : 'rgba(255, 255, 255, 0.1)',
                        color: cookie.required ? 'var(--color-solar)' : 'var(--color-gray-400)'
                      }}>
                        {cookie.required ? 'Required' : 'Optional'}
                      </span>
                    </div>
                  </div>
                  <p style={{ fontSize: '0.875rem', marginBottom: 'var(--space-md)' }}>{cookie.description}</p>
                  <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                    {cookie.examples.map((example, j) => (
                      <li key={j} style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--space-xs)',
                        fontSize: '0.8125rem',
                        color: 'var(--color-gray-400)',
                        marginBottom: 'var(--space-xs)'
                      }}>
                        <span style={{ color: 'var(--color-energy)', width: '14px', height: '14px', flexShrink: 0 }}>
                          {Icons.check}
                        </span>
                        {example}
                      </li>
                    ))}
                  </ul>
                  <p style={{
                    fontSize: '0.75rem',
                    color: 'var(--color-gray-500)',
                    marginTop: 'var(--space-md)',
                    paddingTop: 'var(--space-sm)',
                    borderTop: '1px solid rgba(255, 255, 255, 0.08)'
                  }}>
                    Retention: {cookie.retention}
                  </p>
                </motion.div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Third-Party Cookies */}
      <section className="about-section">
        <div className="container">
          <ScrollReveal className="section-header">
            <h2>Third-Party Cookies</h2>
            <p>Cookies set by our service providers</p>
          </ScrollReveal>

          <ScrollReveal delay={0.1}>
            <div className="legal-content">
              <p>
                Some cookies on our platform are set by third-party services that we use to
                provide certain features. These third parties may collect information about
                your online activities over time and across different websites.
              </p>

              <div style={{ marginTop: 'var(--space-xl)', overflowX: 'auto' }}>
                <table style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  fontSize: '0.875rem'
                }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                      <th style={{ textAlign: 'left', padding: 'var(--space-md)', color: 'var(--color-white)' }}>Provider</th>
                      <th style={{ textAlign: 'left', padding: 'var(--space-md)', color: 'var(--color-white)' }}>Purpose</th>
                      <th style={{ textAlign: 'left', padding: 'var(--space-md)', color: 'var(--color-white)' }}>Type</th>
                    </tr>
                  </thead>
                  <tbody>
                    {thirdPartyCookies.map((cookie, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.05)' }}>
                        <td style={{ padding: 'var(--space-md)', color: 'var(--color-gray-200)' }}>{cookie.name}</td>
                        <td style={{ padding: 'var(--space-md)', color: 'var(--color-gray-400)' }}>{cookie.purpose}</td>
                        <td style={{ padding: 'var(--space-md)' }}>
                          <span style={{
                            fontSize: '0.75rem',
                            padding: '2px 8px',
                            borderRadius: 'var(--radius-sm)',
                            background: cookie.type === 'Essential' ? 'rgba(255, 215, 0, 0.2)' : 'rgba(255, 255, 255, 0.1)',
                            color: cookie.type === 'Essential' ? 'var(--color-solar)' : 'var(--color-gray-400)'
                          }}>
                            {cookie.type}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Managing Cookies */}
      <section className="about-section about-dark">
        <div className="container">
          <ScrollReveal className="section-header">
            <h2>Managing Your Cookie Preferences</h2>
            <p>How to control cookies on your device</p>
          </ScrollReveal>

          <ScrollReveal delay={0.1}>
            <div className="legal-content">
              <div className="legal-section">
                <h3>Cookie Consent</h3>
                <p>
                  When you first visit our website, you will be shown a cookie consent banner that
                  allows you to accept or reject non-essential cookies. You can change your preferences
                  at any time by clicking the "Cookie Settings" link in the footer of our website.
                </p>
              </div>

              <div className="legal-section">
                <h3>Browser Settings</h3>
                <p>
                  Most web browsers allow you to control cookies through their settings. You can
                  typically find these options in your browser's "Privacy" or "Security" settings.
                  Here's how to manage cookies in popular browsers:
                </p>
                <ul style={{ marginTop: 'var(--space-md)', marginLeft: 'var(--space-lg)' }}>
                  <li style={{ marginBottom: 'var(--space-sm)', color: 'var(--color-gray-300)' }}>
                    <strong style={{ color: 'var(--color-white)' }}>Chrome:</strong> Settings → Privacy and security → Cookies
                  </li>
                  <li style={{ marginBottom: 'var(--space-sm)', color: 'var(--color-gray-300)' }}>
                    <strong style={{ color: 'var(--color-white)' }}>Firefox:</strong> Settings → Privacy & Security → Cookies
                  </li>
                  <li style={{ marginBottom: 'var(--space-sm)', color: 'var(--color-gray-300)' }}>
                    <strong style={{ color: 'var(--color-white)' }}>Safari:</strong> Preferences → Privacy → Manage Website Data
                  </li>
                  <li style={{ marginBottom: 'var(--space-sm)', color: 'var(--color-gray-300)' }}>
                    <strong style={{ color: 'var(--color-white)' }}>Edge:</strong> Settings → Cookies and site permissions
                  </li>
                </ul>
              </div>

              <div className="legal-section">
                <h3>Opt-Out Tools</h3>
                <p>
                  You can also opt out of certain third-party cookies using industry opt-out tools:
                </p>
                <ul style={{ marginTop: 'var(--space-md)', marginLeft: 'var(--space-lg)' }}>
                  <li style={{ marginBottom: 'var(--space-sm)', color: 'var(--color-gray-300)' }}>
                    <a href="https://optout.networkadvertising.org" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--color-energy)' }}>
                      Network Advertising Initiative Opt-Out
                    </a>
                  </li>
                  <li style={{ marginBottom: 'var(--space-sm)', color: 'var(--color-gray-300)' }}>
                    <a href="https://optout.aboutads.info" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--color-energy)' }}>
                      Digital Advertising Alliance Opt-Out
                    </a>
                  </li>
                  <li style={{ marginBottom: 'var(--space-sm)', color: 'var(--color-gray-300)' }}>
                    <a href="https://www.youronlinechoices.eu" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--color-energy)' }}>
                      European Interactive Digital Advertising Alliance
                    </a>
                  </li>
                </ul>
              </div>

              <div className="legal-highlight">
                <strong>Note:</strong> Disabling certain cookies may affect the functionality of
                our platform. Essential cookies cannot be disabled as they are necessary for the
                platform to function properly.
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Do Not Track */}
      <section className="about-section">
        <div className="container">
          <ScrollReveal className="section-header">
            <h2>Do Not Track Signals</h2>
            <p>Our response to browser DNT signals</p>
          </ScrollReveal>

          <ScrollReveal delay={0.1}>
            <div className="legal-content">
              <p>
                Some browsers include a "Do Not Track" (DNT) feature that signals to websites
                that you do not want your online activity tracked. Currently, there is no
                universally accepted standard for how to respond to DNT signals.
              </p>
              <p style={{ marginTop: 'var(--space-lg)' }}>
                At this time, we do not respond to DNT signals. However, you can manage your
                cookie preferences using the methods described above, and we will respect your choices.
              </p>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Updates */}
      <section className="about-section about-dark">
        <div className="container">
          <ScrollReveal className="section-header">
            <h2>Changes to This Policy</h2>
            <p>How we will notify you of updates</p>
          </ScrollReveal>

          <ScrollReveal delay={0.1}>
            <div className="legal-content">
              <p>
                We may update this Cookie Policy from time to time to reflect changes in our
                practices or for other operational, legal, or regulatory reasons. When we make
                changes, we will update the "Last updated" date at the top of this policy.
              </p>
              <p style={{ marginTop: 'var(--space-lg)' }}>
                We encourage you to review this Cookie Policy periodically to stay informed about
                how we use cookies and similar technologies.
              </p>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Contact */}
      <section className="draft-cta">
        <div className="container">
          <ScrollReveal className="draft-cta-content">
            <h2>Questions About Cookies?</h2>
            <p>
              If you have any questions about our use of cookies or this Cookie Policy,
              please contact our privacy team.
            </p>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 'var(--space-md)',
              marginBottom: 'var(--space-xl)'
            }}>
              <a
                href="mailto:privacy@ai4solar.com"
                style={{
                  color: 'var(--color-energy)',
                  fontSize: '1.125rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-sm)'
                }}
              >
                <span style={{ width: '20px', height: '20px' }}>{Icons.mail}</span>
                privacy@ai4solar.com
              </a>
            </div>
            <div className="cta-buttons">
              <GlowButton href="/privacy" variant="solar" size="large">
                Privacy Policy
              </GlowButton>
              <GlowButton href="/terms" variant="outline" size="large">
                Terms of Service
              </GlowButton>
            </div>
          </ScrollReveal>
        </div>
      </section>
    </div>
  )
}
